import type { ReactNode } from 'react'
import { PageHelp } from '@/features/help/PageHelp'
import { formatCount } from '@/lib/format'

/** Width the header needs for title, extras and controls on one row (container query). */
export const HEADER_SEARCH_CLASS = 'w-auto min-w-[150px] flex-1 sm:w-auto @[60rem]:w-64 @[60rem]:flex-none'

/**
 * The one-row header every list page uses (the "Clients" template): the page
 * title and its count, view switchers, then the page's controls (search,
 * filters) and actions. No breadcrumbs or description line — the top menu
 * already says where you are, and the row keeps the table high up.
 *
 * It lays out by its own width, not the viewport: wide, everything shares one
 * row with the controls on the right; narrow, the title (and switchers, which
 * scroll sideways rather than wrap) sit above one full-width row of search and
 * buttons. Render it inside its own toolbar Panel above the table's Panel.
 */
export function ListHeader({
  title,
  count,
  extra,
  children,
  actions,
}: {
  /** Left out for a list inside another record: no title, count or page help. */
  title?: ReactNode
  count?: number | null
  /** Next to the title: view switchers, status badges. */
  extra?: ReactNode
  /** Page controls (search first, then filters and tools). */
  children?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="@container">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5">
        {title && (
          <div className="flex min-w-0 shrink-0 items-baseline gap-2">
            <h1 className="truncate text-[length:calc(14px*var(--text-scale))] font-semibold uppercase tracking-wide text-fg">{title}</h1>
            {count !== undefined && count !== null && <span className="num text-[length:calc(12px*var(--text-scale))] text-muted">{formatCount(count)}</span>}
            <PageHelp className="self-center" />
          </div>
        )}
        {extra && <div className="scrollbar-none flex min-w-0 max-w-full items-center gap-2 overflow-x-auto [&>*]:shrink-0">{extra}</div>}
        {(children || actions) && (
          <div className="flex min-w-0 basis-full flex-wrap items-center gap-2 @[60rem]:ml-auto @[60rem]:flex-1 @[60rem]:basis-auto @[60rem]:flex-nowrap @[60rem]:justify-end">
            {children}
            {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
          </div>
        )}
      </div>
    </div>
  )
}
